/**
 * BranchRefresh - the freshness and Refresh strip of a branch-scoped view (SIH-17 I-8b, PD-36 N1 and N9).
 *
 * Used by the failed list (S3), the pending list (S4) and the invoice detail (S5). These views have
 * no period selector and no Change TPIN: the period is set on the branch view and carried in the
 * URL. They show the branch's source timestamp, say plainly that nothing updates by itself, and
 * offer one control, "Refresh this branch", which re-queries this branch's data only (PD-33).
 *
 * The refreshing state follows the S-BRANCH pattern exactly (N9): the control is disabled and reads
 * "Refreshing…", the previous data stays on screen, and the timestamp is replaced only when the
 * answer arrives. Nothing here starts a read on its own.
 */

import type { FreshnessState } from '@domain/tpin'
import { freshnessLabel } from './BranchHero'
import { REFRESHING_LABEL, REFRESH_LABEL, REFRESH_NOTE } from './refreshCopy'

export { REFRESHING_LABEL, REFRESH_LABEL, REFRESH_NOTE }

export function BranchRefresh({
  freshness,
  refreshing,
  onRefresh,
}: {
  readonly freshness: FreshnessState
  readonly refreshing: boolean
  readonly onRefresh: () => void
}) {
  return (
    <section className="dash-hero" aria-label="Branch data context" data-testid="branch-refresh">
      <div className="dash-hero-row">
        <div className="dash-freshness" aria-live="polite" data-testid="dash-freshness">
          <span className="dash-freshness-label">Source data as of:</span>
          <span className="dash-freshness-value" data-testid="dash-freshness-value">
            {freshnessLabel(freshness)}
          </span>
          <span className="dash-freshness-note" data-testid="refresh-note">
            {REFRESH_NOTE}
          </span>
        </div>
        <button
          type="button"
          className="dash-hero-refresh"
          onClick={onRefresh}
          disabled={refreshing}
          aria-busy={refreshing || undefined}
          data-testid="refresh-button"
        >
          {refreshing ? REFRESHING_LABEL : REFRESH_LABEL}
        </button>
      </div>
    </section>
  )
}
